import { inject, Injectable, Injector } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackErrorComponent } from '../../components/snack-error/snack-error.component';
import { GoToUrl } from '../actions/router.action';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { Observable, of, EMPTY } from 'rxjs';
import { catchError, first, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { AuthService } from '../../services/auth.service';
import * as fromAuth from '../actions/auth.action';
import { AuthState } from '../reducers/auth.reducer';
import { selectAuth } from '../selectors/auth.selector';
import { OidcConfig } from '../../conf/conf';



@Injectable()
export class AuthEffects {
    private actions$ = inject(Actions);
    private injector = inject(Injector);
    private snackBar = inject(MatSnackBar);
    private store$ = inject(Store<AuthState>);

    connection$: Observable<Action> = createEffect(() => this.actions$
        .pipe(
            ofType(fromAuth.AUTH_CO),
            switchMap((action: fromAuth.AuthCo) => {
                this.authService.startAuthentication(action.payload);
                return EMPTY;
            }),
            catchError(error => of(new fromAuth.AuthCoFail(error)))
        ), { dispatch: false });

       complete$: Observable<Action> = createEffect(() => this.actions$
        .pipe(
            ofType(fromAuth.AUTH_CALLBACK),
            switchMap(() =>
              this.authService.completeAuthentication()
            ),
            map((redirectUrl: string) =>
              new fromAuth.AuthRegisterRedirectUrl({redirectUrl})
            ),
            catchError(error =>  this.authService.getUserStoreUrl().pipe(
              first(),
              mergeMap(rUrl => of(
                new fromAuth.AuthCoFail(error),
                this.go(this.authService.newGoHome(rUrl)))
              )
            ))
        ));

     registerRedirectUrl$: Observable<Action> = createEffect(() => this.actions$
      .pipe(
          ofType(fromAuth.AUTH_CO_REGISTER_REDIRECT_URL),
          switchMap((action) => this.authService.getUser().pipe(
            map(user => new fromAuth.AuthCoSilent(user))
          )
      ),
    ));

     authSilent$: Observable<Action> = createEffect(() => this.actions$
      .pipe(
        ofType(fromAuth.AUTH_CO_SILENT),
        map(() =>
          this.config.useExtensions ? new fromAuth.AuthCoExtensionBeforeAuthSuccess()
                                                    : new fromAuth.AuthCoExtensionBeforeAuthSuccessResponse()
        ),
      ));

     extensionBeforeAuthSuccessResponse$: Observable<Action> = createEffect(() => this.actions$
      .pipe(
        ofType(fromAuth.AUTH_CO_EXTENSION_BEFORE_AUTH_SUCCESS_RESPONSE),
        map(() => new fromAuth.AuthCoSuccess())
      ));

     afterAuthSuccess$: Observable<Action> = createEffect(() => this.actions$
      .pipe(
        ofType(fromAuth.AUTH_CO_SUCCESS),
        withLatestFrom(this.store$.pipe(select(selectAuth))),
        switchMap(([, storeState]) => storeState.redirectUrl != null ? of(this.go(storeState.redirectUrl)) : EMPTY),
      ));

     connectionError$: Observable<Action> = createEffect(() => this.actions$
      .pipe(
          ofType(fromAuth.AUTH_CO_FAIL),
          switchMap((action: fromAuth.AuthCoFail) => {
              console.log('Error during authentication', action.payload);
              SnackErrorComponent.open(this.snackBar, action.payload);
              return EMPTY;
          })
      ), { dispatch: false });

     deconnection$: Observable<Action> = createEffect(() => this.actions$
        .pipe(
            ofType(fromAuth.AUTH_DECO),
            switchMap((action: fromAuth.AuthDeco) => {
                return this.authService.logout(action.payload.local)
                    .pipe(
                        mergeMap(() => {
                          if (action.payload.local) {
                            return of( new fromAuth.AuthDecoSuccess(), <Action> new fromAuth.LogoutComplete(action.payload));
                          } else {
                            return EMPTY;
                          }
                        }),
                        catchError(error => of(new fromAuth.AuthDecoFail(error)))
                    );
            })
        ));

     logoutSuccess$: Observable<Action> = createEffect(() => this.actions$
    .pipe(
        ofType(fromAuth.AUTH_LOGOUT_COMPLETE),
        map(() =>
          <Action> this.go(this.config.logout_success_uri)
        )
    ));

     deconnectionError$: Observable<Action> = createEffect(() => this.actions$
    .pipe(
        ofType(fromAuth.AUTH_DECO_FAIL),
        switchMap((action: fromAuth.AuthDecoFail) => {
            SnackErrorComponent.open(this.snackBar, action.payload);
            return EMPTY;
        })
    ), { dispatch: false });


    /**
     * Note sur Injector: On utilise injector plutôt que d'injecter directemet authService
     * et config car sinon cela entraine l'initialisation
     * de la config avant les APP_INITIALIZER (dû au chargement des effets ngrx).
     */

    private go(url: string): GoToUrl {
      return new GoToUrl({url: url, extras: { replaceUrl: true }});
    }

    private get authService(): AuthService {
      return this.injector.get(AuthService);
    }

    private get config(): OidcConfig {
      return this.injector.get(OidcConfig);
    }
}

